// Sign-in codes, in the one format both sides use: eight characters, 0-9 and
// A-F. The Worker makes and checks them (worker/directory.ts); the app reads
// them as they are typed or pasted (Welcome and Settings).

/** How many characters a code has. */
export const CODE_LENGTH = 8;

/**
 * A code as the server checks it: upper case, with spaces and dashes taken
 * out. Nothing else is taken out, so words around a code never become part of it.
 */
export function normalizeCode(raw: string): string {
  return raw.replace(/[\s-]+/g, "").toUpperCase();
}

const SHAPE = new RegExp(`^[0-9A-F]{${CODE_LENGTH}}$`);

/** Whether a normalized code has a code's shape. */
export function isCode(code: string): boolean {
  return SHAPE.test(code);
}

// A code standing on its own in some text: eight characters, or four and four
// with one space or dash between, with no letter or digit touching either end.
const HALF = CODE_LENGTH / 2;
const ALONE = new RegExp(`(?:^|[^0-9a-z])([0-9a-f]{${HALF}})[\\s-]?([0-9a-f]{${HALF}})(?![0-9a-z])`, "i");

function findCode(text: string): string | null {
  const m = ALONE.exec(text);
  return m ? (m[1] + m[2]).toUpperCase() : null;
}

/** What an edit put into a field: the text between what stayed the same at either end. */
function inserted(before: string, after: string): string {
  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start]) start++;
  let end = 0;
  while (end < before.length - start && end < after.length - start && before[before.length - 1 - end] === after[after.length - 1 - end]) end++;
  return after.slice(start, after.length - end);
}

/**
 * A code as typed or pasted into a field that held `before`. Pasted text that
 * holds a code on its own gives just that code, so a whole line copied from the
 * email works. Otherwise the field keeps the letters and digits it was given,
 * upper case. A full field takes no more typing until something in it is
 * selected or deleted, as a field with a maximum length does.
 */
export function cleanCode(raw: string, before = ""): string {
  const found = findCode(inserted(before, raw)) ?? findCode(raw);
  if (found) return found;
  const kept = raw.replace(/[^0-9a-f]/gi, "").toUpperCase();
  return kept.length > CODE_LENGTH && before.length === CODE_LENGTH ? before : kept.slice(0, CODE_LENGTH);
}

/** Seconds before another code can be asked for, after `sent` codes: a minute after the first, then twice as long each time. */
export function resendWait(sent: number): number {
  return Math.min(60 * 2 ** Math.max(0, sent - 1), 30 * 60);
}

/** A wait as a short clock: "45s", or "3:05". */
export function clock(seconds: number): string {
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
